import { Fragment, useId, useImperativeHandle, useRef, useState } from 'react';
import type { ComponentProps, ReactNode, Ref } from 'react';

import { SheetHandle } from '@playbook/atoms/sheet-handle';
import { useMediaQuery } from '@playbook/modules/layout/media-query';
import { usePopoverClickGuard } from '@playbook/utils/popover-guard';
import { useSwipeToDismiss } from '@playbook/utils/swipe';

import css from './styles.module.css';

export type PopoutMenuAppearFrom = 'left' | 'right';

const DefaultPopoutMenuAppearFrom: PopoutMenuAppearFrom = 'left' as const;

const PopoutMenuSheetQuery: string = '(max-width: 600px)';

export interface PopoutMenuHandle {
	open(): void;
	close(): void;
}

type PopoutMenuEventHandlers = {
	[K in keyof ComponentProps<'div'> as K extends `on${string}`
		? K
		: never]?: ComponentProps<'div'>[K];
};

interface PopoutMenuDataAttributes {
	[key: `data-${string}`]: string | number | boolean | undefined;
}

interface PopoutMenuProperties
	extends PopoutMenuEventHandlers, PopoutMenuDataAttributes {
	appearFrom?: PopoutMenuAppearFrom;
	/** More room, for a panel that holds rows of fields, such as filters. */
	wide?: boolean;

	children: ReactNode;

	renderTriggerWith?:
		| ((popoverTarget: string, open: boolean) => ReactNode)
		| null;

	ref?: Ref<PopoutMenuHandle>;
}

export function PopoutMenu({
	appearFrom = DefaultPopoutMenuAppearFrom,
	wide = false,

	children,

	renderTriggerWith = null,

	ref,

	onToggle,

	...props
}: PopoutMenuProperties): ReactNode {
	const popover: string = `${useId().replaceAll(':', '')}-popout`;
	const element = useRef<HTMLDivElement>(null);

	const [open, setOpen] = useState<boolean>(false);
	const sheet: boolean = useMediaQuery(PopoutMenuSheetQuery);

	useSwipeToDismiss(element, sheet, () => element.current?.hidePopover());
	usePopoverClickGuard(element, true);

	useImperativeHandle(
		ref,
		() => ({
			open: () => element.current?.showPopover(),
			close: () => element.current?.hidePopover(),
		}),
		[]
	);

	return (
		<Fragment>
			{renderTriggerWith?.(popover, open)}

			<div
				{...props}
				ref={element}
				id={popover}
				popover="auto"
				data-appear-from={appearFrom}
				data-wide={wide}
				data-sheet={sheet}
				className={css.popover}
				onToggle={(event) => {
					onToggle?.(event);
					setOpen(event.newState === 'open');
				}}>
				{sheet && <SheetHandle />}
				{children}
			</div>
		</Fragment>
	);
}
